import { Component, OnInit, forwardRef, OnDestroy, Input } from '@angular/core';

import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
  FormControl,
} from '@angular/forms';

import { takeUntil, tap } from 'rxjs/operators';

import { FUIValueListItem } from '@fry/ui-material/value-list-control';
import { Subject } from 'rxjs';

export interface RefundOptionItem {
  title: string;
  type: 'adjustment'|'fixed'|'custom';
  adjustment: number;
  default: boolean;
  metadata?: FUIValueListItem[];
}

@Component({
    selector: 'app-workflow-transition-action-refund-options-form',
    templateUrl: './refund-options-form.component.html',
    styleUrls: ['./refund-options-form.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WorkflowTransitionActionRefundOptionsFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => WorkflowTransitionActionRefundOptionsFormComponent),
            multi: true
        }
    ],
    standalone: false
})
export class WorkflowTransitionActionRefundOptionsFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {

  @Input() isOptional: boolean = false;

  private destroyed$ = new Subject<void>();

  public form!: FormGroup;

  public get formOptionsArray(): FormArray {
    return this.form.get('options') as FormArray;
  }

  constructor(private formBuilder: FormBuilder) {}

  //
  // Lifecycle
  //

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      options: this.formBuilder.array([], this.isOptional ? [] : [Validators.required])
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private ensureValidDefault(currentDefaultIndex: number): void {
    for (let index = 0; index < this.formOptionsArray.length; index++) {
      const element = this.formOptionsArray.get(`${index}.default`) as FormControl;
      if (currentDefaultIndex !== index && element.value === true) {
        element.setValue(false);
      }
    }
  }

  private createOptionGroup(item?: RefundOptionItem): FormGroup {
    const controls: { [K in keyof RefundOptionItem]: any } = {
      title: [item ? item.title : '', Validators.required],
      type: [item ? item.type : '', Validators.required],
      adjustment: [item ? item.adjustment : 0, Validators.required],
      default: [item ? item.default : false],
      metadata: [item ? item.metadata : []]
    };
    return this.formBuilder.group(controls);
  }

  //
  // Validator
  //

  validate(_control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {
      invalidRefundOptionsForm: {
        valid: false,
        message: $localize `Refund options fields are invalid`
      }
    };
    // tslint:disable-next-line: no-null-keyword
    return this.form.valid ? null : errors;
  }

  //
  // ControlValueAccessor
  //

  public onTouched: () => void = () => { };

  writeValue(value: any): void {
    if (!value) { return; }

    const items: RefundOptionItem[] = value.options ?? [];
    items.map(item => this.createOptionGroup(item))
         .forEach(group => this.formOptionsArray.push(group));
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges
             .pipe(
               tap(() => this.onTouched()),
               takeUntil(this.destroyed$)
             )
             .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  //
  // Actions
  //

  public onAdd(): void {
    this.formOptionsArray.push(this.createOptionGroup());
  }

  public onDelete(index: number): void {
    this.formOptionsArray.removeAt(index);
  }

  public onToggleDefault(index: number): void {
    this.ensureValidDefault(index);
  }

  //
  // Template helpers
  //

  public get isEmpty(): boolean {
    return this.formOptionsArray.length === 0;
  }

  public optionMetadataTitle(index: number): string|undefined {
    const control = this.formOptionsArray.get(`${index}.title`) as FormControl;
    if (control && control.value) {
      return $localize `Metadata for option '${control.value}'`;
    } else {
      return $localize `Metadata`;
    }
  }
}
